<script setup>
import { computed, ref, watch } from 'vue';
import { useStore } from 'dashboard/composables/store';
import { useAlert } from 'dashboard/composables';
import ContactAPI from 'dashboard/api/contacts';

import Dialog from 'dashboard/components-next/dialog/Dialog.vue';

const props = defineProps({
  contact: {
    type: Object,
    required: true,
  },
});

const store = useStore();

const STATUS_KEY = 'mayara_status_comercial';
const ENTRY_KEY = 'mayara_data_entrada_lead';
const ORIGIN_KEY = 'mayara_origem_lead';
const OWNER_KEY = 'mayara_responsavel_comercial';
const RECOVERY_DATE_KEY = 'mayara_proxima_repescagem';
const RECOVERY_TYPE_KEY = 'mayara_tipo_repescagem';

const isSaving = ref(false);
const owner = ref('');

const closingDialogRef = ref(null);
const closingMode = ref('30');
const customClosingDate = ref('');

const notClosedDialogRef = ref(null);
const notClosedReason = ref('');
const scheduleNotClosedRecovery = ref(false);
const notClosedRecoveryMode = ref('30');
const customNotClosedRecoveryDate = ref('');

const attributes = computed(() => props.contact?.custom_attributes || {});
const isCommercial = computed(() => Boolean(attributes.value[STATUS_KEY]));
const currentStatus = computed(() => attributes.value[STATUS_KEY] || '');
const selectedStatus = ref(currentStatus.value);

watch(currentStatus, status => {
  selectedStatus.value = status;
});

const statuses = [
  { value: 'novo', label: 'Novo' },
  { value: 'em_atendimento', label: 'Em atendimento' },
  { value: 'aguardando', label: 'Aguardando' },
  { value: 'nao_fechou', label: 'Não fechou' },
  { value: 'fechado', label: 'Fechado' },
];

const origins = [
  { value: '', label: 'Origem não informada' },
  { value: 'instagram', label: 'Instagram' },
  { value: 'whatsapp', label: 'WhatsApp' },
  { value: 'indicacao', label: 'Indicação' },
  { value: 'google', label: 'Google' },
  { value: 'outro', label: 'Outro' },
];

const closingOptions = [
  { value: '30', label: '30 dias' },
  { value: '45', label: '45 dias' },
  { value: '60', label: '60 dias' },
  { value: '90', label: '90 dias' },
  { value: 'custom', label: 'Escolher data' },
];

const notClosedRecoveryOptions = [
  { value: '7', label: '7 dias' },
  { value: '15', label: '15 dias' },
  { value: '30', label: '30 dias' },
  { value: '60', label: '60 dias' },
  { value: 'custom', label: 'Escolher data' },
];

const localDateString = date => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

const today = () => localDateString(new Date());

const addDays = days => {
  const date = new Date();
  date.setHours(12, 0, 0, 0);
  date.setDate(date.getDate() + Number(days));
  return localDateString(date);
};

const nextContactDate = computed(() => {
  if (closingMode.value === 'custom') {
    return customClosingDate.value;
  }

  return addDays(closingMode.value);
});

const formattedNextContactDate = computed(() => {
  if (!nextContactDate.value) return '';

  const [year, month, day] = nextContactDate.value.split('-').map(Number);
  const date = new Date(year, month - 1, day, 12, 0, 0);

  return new Intl.DateTimeFormat('pt-BR').format(date);
});

const isClosingInvalid = computed(() => {
  if (!nextContactDate.value) return true;
  return nextContactDate.value <= today();
});

const notClosedRecoveryDate = computed(() => {
  if (!scheduleNotClosedRecovery.value) return '';

  if (notClosedRecoveryMode.value === 'custom') {
    return customNotClosedRecoveryDate.value;
  }

  return addDays(notClosedRecoveryMode.value);
});

const isNotClosedRecoveryInvalid = computed(() => {
  if (!scheduleNotClosedRecovery.value) return false;
  if (!notClosedRecoveryDate.value) return true;
  return notClosedRecoveryDate.value <= today();
});

const isNotClosedInvalid = computed(
  () => !notClosedReason.value.trim() || isNotClosedRecoveryInvalid.value
);

const formattedNotClosedRecoveryDate = computed(() => {
  if (!notClosedRecoveryDate.value) return '';

  const [year, month, day] = notClosedRecoveryDate.value
    .split('-')
    .map(Number);

  return new Intl.DateTimeFormat('pt-BR').format(
    new Date(year, month - 1, day, 12, 0, 0)
  );
});

const formatStoredDate = value => {
  if (!value) return '';

  const [year, month, day] = value.split('-').map(Number);
  if (!year || !month || !day) return value;

  return new Intl.DateTimeFormat('pt-BR').format(
    new Date(year, month - 1, day, 12, 0, 0)
  );
};

const statusLabel = computed(
  () =>
    statuses.find(item => item.value === currentStatus.value)?.label ||
    currentStatus.value
);

const statusClass = computed(() => {
  const classes = {
    novo: 'bg-n-blue-3 text-n-blue-11',
    em_atendimento: 'bg-n-teal-3 text-n-teal-11',
    aguardando: 'bg-n-amber-3 text-n-amber-11',
    nao_fechou: 'bg-n-ruby-3 text-n-ruby-11',
    fechado: 'bg-n-green-3 text-n-green-11',
  };

  return classes[currentStatus.value] || 'bg-n-alpha-2 text-n-slate-10';
});

const saveAttributes = async customAttributes => {
  if (!props.contact?.id || isSaving.value) return false;

  isSaving.value = true;

  try {
    await store.dispatch('contacts/update', {
      id: props.contact.id,
      customAttributes,
    });
    return true;
  } catch (error) {
    useAlert('Não foi possível atualizar os dados comerciais.');
    return false;
  } finally {
    isSaving.value = false;
  }
};

const updateAttribute = async (key, value) => {
  const customAttributes = {
    ...attributes.value,
  };

  if (value) {
    customAttributes[key] = value;
  } else {
    delete customAttributes[key];
  }

  return saveAttributes(customAttributes);
};

const addToCommercial = async () => {
  if (!props.contact?.id || isSaving.value) return;

  isSaving.value = true;

  try {
    await ContactAPI.addToCommercial(props.contact.id);

    await store.dispatch('contacts/show', {
      id: props.contact.id,
    });

    useAlert('Contato adicionado ao comercial.');
  } catch (error) {
    useAlert('Não foi possível adicionar o contato ao comercial.');
  } finally {
    isSaving.value = false;
  }
};

const removeFromCommercial = async () => {
  if (!props.contact?.id || isSaving.value) return;

  isSaving.value = true;

  try {
    await store.dispatch('contacts/deleteCustomAttributes', {
      id: props.contact.id,
      customAttributes: [
        STATUS_KEY,
        RECOVERY_DATE_KEY,
        RECOVERY_TYPE_KEY,
      ],
    });

    useAlert('Contato removido do funil comercial.');
  } catch (error) {
    useAlert('Não foi possível remover o contato do comercial.');
  } finally {
    isSaving.value = false;
  }
};

const openClosingDialog = () => {
  closingMode.value = '30';
  customClosingDate.value = '';
  closingDialogRef.value?.open();
};

const openNotClosedDialog = () => {
  notClosedReason.value = '';
  scheduleNotClosedRecovery.value = false;
  notClosedRecoveryMode.value = '30';
  customNotClosedRecoveryDate.value = '';
  notClosedDialogRef.value?.open();
};

const changeStatus = async () => {
  const value = selectedStatus.value;

  if (!value || value === currentStatus.value) return;

  if (value === 'fechado') {
    selectedStatus.value = currentStatus.value;
    setTimeout(() => openClosingDialog(), 0);
    return;
  }

  if (value === 'nao_fechou') {
    selectedStatus.value = currentStatus.value;
    setTimeout(() => openNotClosedDialog(), 0);
    return;
  }

  const saved = await updateAttribute(STATUS_KEY, value);

  if (!saved) {
    selectedStatus.value = currentStatus.value;
  }
};

const confirmNotClosed = async () => {
  if (isNotClosedInvalid.value) return;

  const customAttributes = {
    ...attributes.value,
    [STATUS_KEY]: 'nao_fechou',
    mayara_motivo_nao_fechamento: notClosedReason.value.trim(),
  };

  if (scheduleNotClosedRecovery.value) {
    customAttributes[RECOVERY_DATE_KEY] = notClosedRecoveryDate.value;
    customAttributes[RECOVERY_TYPE_KEY] = 'nao_fechou';
  } else {
    delete customAttributes[RECOVERY_DATE_KEY];
    delete customAttributes[RECOVERY_TYPE_KEY];
  }

  const saved = await saveAttributes(customAttributes);

  if (!saved) return;

  notClosedDialogRef.value?.close();
  selectedStatus.value = 'nao_fechou';

  if (scheduleNotClosedRecovery.value) {
    useAlert(
      `Lead marcado como não fechado. Nova abordagem programada para ${formattedNotClosedRecoveryDate.value}.`
    );
  } else {
    useAlert('Lead marcado como não fechado.');
  }
};

const confirmClosing = async () => {
  if (isClosingInvalid.value) {
    useAlert('Escolha uma data futura para o próximo contato.');
    return;
  }

  if (!props.contact?.id || isSaving.value) return;

  isSaving.value = true;

  try {
    await ContactAPI.closeCommercial(
      props.contact.id,
      nextContactDate.value
    );

    await store.dispatch('contacts/show', {
      id: props.contact.id,
    });

    closingDialogRef.value?.close();

    useAlert(
      `Lead fechado. Próximo contato programado para ${formattedNextContactDate.value}.`
    );
  } catch (error) {
    useAlert('Não foi possível concluir o fechamento deste lead.');
  } finally {
    isSaving.value = false;
  }
};

const saveOwner = async () => {
  const value = owner.value.trim();
  if (!value) return;

  const saved = await updateAttribute(OWNER_KEY, value);

  if (saved) {
    owner.value = '';
  }
};
</script>

<template>
  <div class="px-4 pb-3">
    <div
      class="flex flex-col gap-3 p-3 border rounded-xl border-n-weak bg-n-alpha-1"
    >
      <div class="flex items-center justify-between gap-3">
        <div class="flex items-center gap-2 min-w-0">
          <span
            class="flex items-center justify-center w-8 h-8 rounded-lg bg-n-blue-3 text-n-blue-11 shrink-0"
          >
            <i class="i-lucide-briefcase-business" />
          </span>

          <div class="min-w-0">
            <p class="mb-0 text-sm font-medium text-n-slate-12">
              Mayara Care · Comercial
            </p>
            <p class="mb-0 text-xs text-n-slate-9">
              Gestão comercial deste contato
            </p>
          </div>
        </div>

        <span
          v-if="isCommercial"
          class="px-2 py-1 text-xs font-medium rounded-md shrink-0"
          :class="statusClass"
        >
          {{ statusLabel }}
        </span>
      </div>

      <template v-if="!isCommercial">
        <p class="mb-0 text-xs leading-5 text-n-slate-10">
          Este contato ainda não faz parte do funil comercial.
        </p>

        <button
          type="button"
          class="flex items-center justify-center gap-2 px-3 py-2 text-sm font-medium transition-all border rounded-lg border-n-blue-5 bg-n-blue-3 text-n-blue-11 hover:bg-n-blue-4 hover:border-n-blue-7 disabled:opacity-50"
          :disabled="isSaving"
          @click="addToCommercial"
        >
          <i
            :class="
              isSaving
                ? 'i-lucide-loader-circle animate-spin'
                : 'i-lucide-user-plus'
            "
          />
          {{ isSaving ? 'Adicionando...' : 'Adicionar ao comercial' }}
        </button>
      </template>

      <template v-else>
        <div class="grid gap-2">
          <label class="text-xs font-medium text-n-slate-10">
            Status comercial
          </label>

          <select
            v-model="selectedStatus"
            :disabled="isSaving"
            class="w-full px-3 py-2 text-sm border rounded-lg outline-none border-n-weak bg-n-background text-n-slate-11 disabled:opacity-50"
            @change="changeStatus"
          >
            <option
              v-for="status in statuses"
              :key="status.value"
              :value="status.value"
            >
              {{ status.label }}
            </option>
          </select>
        </div>

        <div class="grid gap-2">
          <label class="text-xs font-medium text-n-slate-10">
            Origem
          </label>

          <select
            :value="attributes[ORIGIN_KEY] || ''"
            :disabled="isSaving"
            class="w-full px-3 py-2 text-sm border rounded-lg outline-none border-n-weak bg-n-background text-n-slate-11 disabled:opacity-50"
            @change="updateAttribute(ORIGIN_KEY, $event.target.value)"
          >
            <option
              v-for="item in origins"
              :key="item.value"
              :value="item.value"
            >
              {{ item.label }}
            </option>
          </select>
        </div>

        <div class="grid gap-2">
          <label class="text-xs font-medium text-n-slate-10">
            Responsável
          </label>

          <div class="flex gap-2">
            <input
              v-model="owner"
              type="text"
              :placeholder="attributes[OWNER_KEY] || 'Informe o responsável'"
              :disabled="isSaving"
              class="min-w-0 flex-1 px-3 py-2 text-sm border rounded-lg outline-none border-n-weak bg-n-background text-n-slate-11 disabled:opacity-50"
              @keyup.enter="saveOwner"
            />

            <button
              type="button"
              class="flex items-center justify-center w-9 h-9 border rounded-lg border-n-weak text-n-slate-10 hover:bg-n-alpha-2 disabled:opacity-50"
              :disabled="isSaving || !owner.trim()"
              title="Salvar responsável"
              @click="saveOwner"
            >
              <i
                :class="
                  isSaving
                    ? 'i-lucide-loader-circle animate-spin'
                    : 'i-lucide-check'
                "
              />
            </button>
          </div>
        </div>

        <div
          v-if="attributes[RECOVERY_DATE_KEY]"
          class="flex items-start gap-2 p-2.5 rounded-lg bg-n-green-3 text-n-green-11"
        >
          <i class="i-lucide-calendar-clock mt-0.5 shrink-0" />
          <div>
            <p class="mb-0 text-xs font-medium">Próximo contato programado</p>
            <p class="mb-0 text-xs">
              {{ formatStoredDate(attributes[RECOVERY_DATE_KEY]) }}
            </p>
          </div>
        </div>

        <button
          type="button"
          class="flex items-center justify-center gap-2 px-3 py-2 text-xs font-medium transition-colors rounded-lg text-n-ruby-11 hover:bg-n-ruby-3 disabled:opacity-50"
          :disabled="isSaving"
          @click="removeFromCommercial"
        >
          <i class="i-lucide-circle-minus" />
          Remover do comercial
        </button>
      </template>
    </div>

    <Dialog
      ref="notClosedDialogRef"
      type="edit"
      width="md"
      title="Marcar como não fechou"
      description="Registre o motivo e, se fizer sentido, programe uma nova abordagem."
      confirm-button-label="Confirmar não fechamento"
      cancel-button-label="Cancelar"
      :disable-confirm-button="isNotClosedInvalid"
      :is-loading="isSaving"
      @confirm="confirmNotClosed"
    >
      <div class="flex flex-col gap-5">
        <div class="flex flex-col gap-2">
          <label class="text-sm font-medium text-n-slate-12">
            Motivo do não fechamento
          </label>

          <textarea
            v-model="notClosedReason"
            rows="3"
            maxlength="500"
            placeholder="Ex.: achou o valor alto, decidiu aguardar, não respondeu..."
            class="w-full px-3 py-2 text-sm border rounded-lg outline-none resize-y border-n-weak bg-n-background text-n-slate-11"
          />

          <p class="mb-0 text-xs text-n-slate-8">
            Este campo é obrigatório.
          </p>
        </div>

        <label
          class="flex items-start gap-3 p-3 border rounded-lg cursor-pointer border-n-weak bg-n-alpha-1"
        >
          <input
            v-model="scheduleNotClosedRecovery"
            type="checkbox"
            class="mt-0.5"
          />

          <div>
            <p class="mb-0 text-sm font-medium text-n-slate-12">
              Programar nova abordagem
            </p>
            <p class="mb-0 mt-1 text-xs leading-5 text-n-slate-9">
              O lead aparecerá na fila de Repescagem na data escolhida.
            </p>
          </div>
        </label>

        <template v-if="scheduleNotClosedRecovery">
          <div class="flex flex-col gap-2">
            <label class="text-sm font-medium text-n-slate-12">
              Quando tentar novamente?
            </label>

            <div class="grid grid-cols-2 gap-2 sm:grid-cols-5">
              <button
                v-for="option in notClosedRecoveryOptions"
                :key="option.value"
                type="button"
                class="px-3 py-2 text-xs font-medium transition-all border rounded-lg"
                :class="
                  notClosedRecoveryMode === option.value
                    ? 'border-n-blue-7 bg-n-blue-3 text-n-blue-11'
                    : 'border-n-weak bg-n-background text-n-slate-10 hover:bg-n-alpha-2'
                "
                @click="notClosedRecoveryMode = option.value"
              >
                {{ option.label }}
              </button>
            </div>
          </div>

          <div
            v-if="notClosedRecoveryMode === 'custom'"
            class="flex flex-col gap-2"
          >
            <label class="text-sm font-medium text-n-slate-12">
              Data da nova abordagem
            </label>

            <input
              v-model="customNotClosedRecoveryDate"
              type="date"
              :min="addDays(1)"
              class="w-full px-3 py-2 text-sm border rounded-lg outline-none border-n-weak bg-n-background text-n-slate-11"
            />
          </div>

          <div
            v-if="notClosedRecoveryDate && !isNotClosedRecoveryInvalid"
            class="flex items-start gap-3 p-3 rounded-lg bg-n-blue-3 text-n-blue-11"
          >
            <i class="i-lucide-calendar-check mt-0.5 shrink-0" />

            <div>
              <p class="mb-0 text-sm font-medium">
                Nova abordagem programada
              </p>
              <p class="mb-0 text-xs">
                Este lead voltará para a equipe em
                {{ formattedNotClosedRecoveryDate }}.
              </p>
            </div>
          </div>
        </template>

        <p class="mb-0 text-xs leading-5 text-n-slate-9">
          O lead ficará com status Não fechou. Sem uma nova abordagem
          programada, ele não aparecerá na fila de Repescagem.
        </p>
      </div>
    </Dialog>

    <Dialog
      ref="closingDialogRef"
      type="edit"
      width="md"
      title="Marcar como fechado"
      description="A conversão será registrada e o próximo contato para renovação precisa ser programado."
      confirm-button-label="Confirmar fechamento"
      cancel-button-label="Cancelar"
      :disable-confirm-button="isClosingInvalid"
      :is-loading="isSaving"
      @confirm="confirmClosing"
    >
      <div class="flex flex-col gap-5">
        <div class="flex flex-col gap-2">
          <label class="text-sm font-medium text-n-slate-12">
            Quando devemos entrar em contato novamente?
          </label>

          <div class="grid grid-cols-2 gap-2 sm:grid-cols-5">
            <button
              v-for="option in closingOptions"
              :key="option.value"
              type="button"
              class="px-3 py-2 text-xs font-medium transition-all border rounded-lg"
              :class="
                closingMode === option.value
                  ? 'border-n-blue-7 bg-n-blue-3 text-n-blue-11'
                  : 'border-n-weak bg-n-background text-n-slate-10 hover:bg-n-alpha-2'
              "
              @click="closingMode = option.value"
            >
              {{ option.label }}
            </button>
          </div>
        </div>

        <div v-if="closingMode === 'custom'" class="flex flex-col gap-2">
          <label class="text-sm font-medium text-n-slate-12">
            Data do próximo contato
          </label>

          <input
            v-model="customClosingDate"
            type="date"
            :min="addDays(1)"
            class="w-full px-3 py-2 text-sm border rounded-lg outline-none border-n-weak bg-n-background text-n-slate-11"
          />
        </div>

        <div
          v-if="nextContactDate && !isClosingInvalid"
          class="flex items-start gap-3 p-3 rounded-lg bg-n-blue-3 text-n-blue-11"
        >
          <i class="i-lucide-calendar-check mt-0.5 shrink-0" />

          <div>
            <p class="mb-0 text-sm font-medium">
              Renovação programada
            </p>
            <p class="mb-0 text-xs">
              A equipe deverá procurar este contato em
              {{ formattedNextContactDate }}.
            </p>
          </div>
        </div>

        <p class="mb-0 text-xs leading-5 text-n-slate-9">
          O contato continuará com status Fechado. A data acima será usada
          posteriormente pela fila de Repescagem/Renovação da Mayara Care.
        </p>
      </div>
    </Dialog>
  </div>
</template>
